import { useState } from 'react';
import { useMutation } from 'convex/react';
import { useNavigate } from 'react-router';
import { Building2, Plus } from 'lucide-react';
import { api } from '@bill-alps/backend/convex/_generated/api';
import type { Id } from '@bill-alps/backend/convex/_generated/dataModel';
import { Button, Select, cn } from '@bill-alps/ui';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useTranslation } from '../i18n/useTranslation';
import { AddMandateDialog } from './add-mandate-dialog';

interface CompanySwitcherProps {
  className?: string;
  onSwitchSuccess?: () => void;
}

export function CompanySwitcher({
  className,
  onSwitchSuccess,
}: CompanySwitcherProps = {}) {
  const { t } = useTranslation(['common', 'fiduciaire']);
  const { user, availableCompanies, isFiduciaire, fiduciaire } =
    useCurrentUser();
  const switchCompany = useMutation(
    api.features.users.companies.switchActiveCompany,
  );
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);
  const [addMandateOpen, setAddMandateOpen] = useState(false);
  const activeId = user?.company?._id;
  const canCreateMandate =
    isFiduciaire &&
    availableCompanies.some((company) => company.role === 'owner');
  const hasMultipleCompanies = availableCompanies.length > 1;

  // Ordinary accounts keep the selector when they already have several
  // companies, but only classified trustees can add another mandate.
  if (!activeId || (!hasMultipleCompanies && !canCreateMandate)) {
    return null;
  }

  const toOption = (entry: (typeof availableCompanies)[number]) => ({
    value: entry.companyId,
    label: entry.name,
  });
  // Trustees get the list grouped so their own company never reads as a
  // mandate; everyone else keeps the flat list.
  const ownCompanyId = fiduciaire?.ownCompanyId;
  const own = availableCompanies.filter(
    (entry) => entry.companyId === ownCompanyId,
  );
  const mandates = availableCompanies.filter(
    (entry) => entry.role === 'owner' && entry.companyId !== ownCompanyId,
  );
  const joined = availableCompanies.filter((entry) => entry.role !== 'owner');
  const options =
    isFiduciaire && own.length > 0
      ? [
          {
            label: t('common:companySwitcher.groupOwn'),
            options: own.map(toOption),
          },
          ...(mandates.length > 0
            ? [
                {
                  label: t('common:companySwitcher.groupMandates'),
                  options: mandates.map(toOption),
                },
              ]
            : []),
          ...(joined.length > 0
            ? [
                {
                  label: t('common:companySwitcher.groupOther'),
                  options: joined.map(toOption),
                },
              ]
            : []),
        ]
      : availableCompanies.map(toOption);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {hasMultipleCompanies && (
        <>
          <Building2 className="size-4 shrink-0 text-muted-foreground" />
          <Select
            size="sm"
            value={activeId}
            options={options}
            disabled={switching}
            aria-label={t('common:companySwitcher.label')}
            className="w-48 min-w-0"
            triggerClassName="truncate"
            onChange={(companyId) => {
              if (companyId === activeId) return;
              setSwitching(true);
              // Navigate before the mutation resolves: switching to a mandate
              // flips isMandateSelected, and a reactive OwnCompanyGate redirect
              // (e.g. away from /account) would otherwise race this navigation.
              navigate('/dashboard');
              void switchCompany({ companyId: companyId as Id<'companies'> })
                .then(() => onSwitchSuccess?.())
                .finally(() => setSwitching(false));
            }}
          />
        </>
      )}
      {canCreateMandate && (
        <>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label={t('fiduciaire:addMandate.title')}
            onClick={() => setAddMandateOpen(true)}
          >
            <Plus className="size-4" />
          </Button>
          <AddMandateDialog
            open={addMandateOpen}
            onOpenChange={setAddMandateOpen}
          />
        </>
      )}
    </div>
  );
}
